import { createContext, ReactNode, useContext, useState } from 'react';
import { HatComputedMetricQuery, HatInterval } from './../HatMetrics';

export type HatDbDataContextValue = {
    getData: <T>(dataContextKey: string, interval: HatInterval) => T | undefined;
    setData: <T>(dataContextKey: string, interval: HatInterval, data: T) => void;
};

const HatDbDataContext = createContext<HatDbDataContextValue | null>(null);

/**
 * A dashboard data context is a shared container where dashboard widgets can publish data that they display on the
 * dashboard. For example the total actions is derived from the data published by the channel attribution table.
 * @returns A dashboard data context.
 */
export const useHatDbDataContext = (): HatDbDataContextValue | null => {
    return useContext(HatDbDataContext);
};

export const HatDbDataContextProvider = ({ children }: { children: ReactNode }) => {
    const [cache, setCache] = useState<any>({});

    function getKey(dataContextKey: string, interval: string) {
        return `${dataContextKey}${interval}`;
    }

    function getData(dataContextKey: string, interval: string): any {
        return cache[getKey(dataContextKey, interval)];
    }

    /**
     * Put data onto the dashboard context. The contract for data on the context is that if a value is null or
     * undefined, the underlying query is still "pending". Otherwise, the value should be defined.
     * @param dataContextKey A key to identify this data on the context.
     * @param interval An interval for the data like "current" or "previous".
     * @param data The data to be stored.
     */
    function setData(dataContextKey: string, interval: string, data: any): void {
        const key = getKey(dataContextKey, interval);
        if (cache[key] !== data) {
            let value: any = { ...cache };
            value[key] = data;
            setCache(value);
        }
    }

    const contextValue: HatDbDataContextValue = {
        getData,
        setData,
    };

    return <HatDbDataContext.Provider value={contextValue}>{children}</HatDbDataContext.Provider>;
};

// Return value for a computed metric query when the result is pending.
const pendingQuery = {
    data: undefined,
    isPending: true,
    isSuccess: false,
};

// Return value for a computed metric query when the query is not enabled.
const disabledQuery = {
    data: undefined,
    isPending: false,
    isSuccess: false,
};

/**
 * Compute a value from the data context using an interface that is superficially similar to useQuery.
 * @param hatMetricQuery A computed metric definition.
 * @returns
 */
export function useHatComputedMetric(hatMetricQuery: HatComputedMetricQuery, enabled?: boolean): any {
    const context = useHatDbDataContext();
    if (!(enabled ?? true)) {
        return disabledQuery;
    }

    if (context != null && hatMetricQuery.compute) {
        const data = hatMetricQuery.compute(hatMetricQuery, context);
        if (data !== undefined && data !== null) {
            return {
                data,
                isPending: false,
                isSuccess: true,
            };
        }
    }

    return pendingQuery;
}
